import { defineStore } from 'pinia'
import { supabase } from '@/supabase.js'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreUsers } from '@/stores/storeUsers'
import { useStoreFeed } from '@/stores/storeFeed'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreTitleLogs } from '@/stores/storeTitleLogs'
import { useStoreReferrals } from '@/stores/storeReferrals'
import { useTestMode } from '@/use/useTestMode'

// GoTrue stores phone numbers with no national trunk zero: 447957160941,
// not 4407957160941. Send and verify must build the number identically or
// verification looks up a number that was never sent a code.
const buildPhoneNumber = (phoneCountry, phone) =>
  `+${phoneCountry}${String(phone || '').replace(/^0+/, '')}`

// One initialisation, however many callers: the router starts it on a cold
// load of a protected page and App.vue starts it on mount, and the second
// caller must not subscribe a second auth listener while the first is still
// checking test mode.
let initPromise = null

export const useStoreAuth = defineStore('storeAuth', {
  // Session persistence is handled exclusively by the Supabase client
  // (persistSession: true in supabase.js). Persisting session in Pinia
  // created a dual source of truth that caused stale-token bugs where
  // the user appeared logged in but authenticated requests failed.
  state: () => {
    return {
      user: null,
      session: null,
      authError: null,
      authDebug: null,
      inited: false,
      loading: false,
      userNotAuthed: false,
      userLoaded: false,
      _lastTokenRefresh: null,
      _lastSessionValidation: null,
      _sessionWatchdogTimer: null,
      _visibilityHandler: null,
      _isValidatingSession: false,
      _watchdogFailureCount: 0
    }
  },
  actions: {
    init() {
      if (!initPromise) initPromise = this._init()
      return initPromise
    },

    async _init() {
      // Guard against duplicate initialization (prevents multiple listeners)
      if (this.inited) {
        console.log('[storeAuth] Already initialized, skipping')
        return
      }

      // Check for test mode first (dev only)
      const testMode = useTestMode()
      const isTestModeActive = await testMode.initTestMode()

      if (isTestModeActive) {
        // Inject test user into this store
        testMode.injectIntoAuthStore(this)
        // Apply mobile simulation if requested
        testMode.applyMobileSimulation()
        // Inject test profile into profile store
        const storeProfile = useStoreProfile()
        testMode.injectIntoProfileStore(storeProfile)
        // Initialize remaining stores with test user (skip profile since we injected it)
        this.initStoresTestMode()
        this.inited = true
        console.log('[storeAuth] Test mode active, skipping normal auth flow')
        return
      }

      // Normal auth flow
      supabase.auth.onAuthStateChange((event, session) => {
        console.log(`[storeAuth] Auth state change: ${event}`)

        if (event === 'INITIAL_SESSION') {
          if (session) {
            this.session = session
            this.getUserInfoFromSupabase()
          } else {
            // No session — clear any stale persisted session and mark as guest
            this.session = null
            this.userNotAuthed = true
            this.userLoaded = true
          }
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('[storeAuth] Token refreshed successfully')
          this.session = session
          this._lastTokenRefresh = Date.now()
        } else if (event === 'SIGNED_OUT') {
          // Inline cleanup to avoid calling clearSession() which calls signOut() again
          this.stopSessionWatchdog()

          const clearSignOutState = () => {
            this.user = null
            this.session = null
            this.userNotAuthed = true
            this.userLoaded = true
            this.clearLocalStorageCache()
            // Reset PostHog
            if (import.meta.env.VITE_APP_MODE === 'production') {
              import('@/composables/usePostHog').then(({ usePostHog }) => {
                const { reset } = usePostHog()
                reset()
              })
            }
          }

          // Navigate to auth BEFORE clearing state to avoid flash of guest UI
          // router.push() is async — defer state clearing until navigation completes
          const signOutRoute = this.router?.currentRoute?.value
          if (signOutRoute && signOutRoute.name !== 'auth') {
            this.router
              .push({ name: 'auth' })
              .then(clearSignOutState)
              .catch(clearSignOutState)
          } else {
            clearSignOutState()
          }
        } else if (event === 'USER_UPDATED') {
          console.log('[storeAuth] User updated')
          if (session) {
            this.session = session
            this.user = session.user
          }
        }
      })
      this.userLoaded = false
      this.userNotAuthed = false
      this.inited = true
    },

    async getUserInfoFromSupabase() {
      try {
        let result = await supabase.auth.getUser()

        // Retry once after 1s — covers transient network failures on
        // iOS resume where connectivity isn't immediately available
        if (result.error) {
          console.warn('[storeAuth] getUser failed, retrying in 1s:', result.error?.message)
          await new Promise((r) => setTimeout(r, 1000))
          result = await supabase.auth.getUser()
        }

        if (result.error) throw result.error
        this.user = result.data.user

        // Identify user in PostHog on session restoration
        if (import.meta.env.VITE_APP_MODE === 'production' && result.data.user?.id) {
          import('@/composables/usePostHog').then(({ usePostHog }) => {
            const { identify } = usePostHog()
            identify(result.data.user.id, {
              email: result.data.user.email,
              phone: result.data.user.phone
            })
          })
        }

        this.initStores()
        this.startSessionWatchdog()
      } catch (error) {
        console.warn('[storeAuth] getUser failed after retry, soft-invalidating session:', error?.message || error)
        // Soft invalidation preserves the refresh token so Supabase can
        // auto-recover on the next cold start
        await this.invalidateSession()
      } finally {
        this.userLoaded = true
      }
    },

    async initStores() {
      // Only initialize user-dependent stores when authenticated
      if (!this.user) {
        console.log('Skipping store initialization - user not authenticated')
        return
      }

      const storeProfile = useStoreProfile()

      // Initialize profile first to check deletion status
      await storeProfile.init()

      // If user is pending deletion, don't initialize other stores
      if (storeProfile.currentUserPendingDeletion) {
        console.log(
          'User pending deletion - skipping other store initialization'
        )
        return
      }

      const storeReviews = useStoreReviews()
      const storeLists = useStoreLists()
      const storeUsers = useStoreUsers()
      const storeFeed = useStoreFeed()
      const storeTitlesUser = useStoreTitlesUser()
      const storeTitleLogs = useStoreTitleLogs()
      const storeReferrals = useStoreReferrals()

      storeReviews.init()
      storeUsers.init()
      storeFeed.init()
      storeLists.init()
      storeTitlesUser.init()
      storeTitleLogs.init()
      storeReferrals.init()
    },

    // Test mode initialization - skips profile init since it's already injected
    initStoresTestMode() {
      if (!this.user) {
        console.log('Skipping store initialization - user not authenticated')
        return
      }

      // Profile is already injected by test mode, just init other stores
      // Note: In test mode, these stores may not have real data but the app should render
      console.log(
        '[storeAuth] Test mode: initializing stores (profile already injected)'
      )
    },

    updateSessionFromSupabaseSession(session) {
      this.session = session
    },

    async handleLoginEmail(email, allowCreateUser = false) {
      this.authDebug = supabase.supabaseUrl
      this.authError = null
      try {
        this.loading = true
        // const { data, error } = await supabase.auth.signIn({ email: email.value })
        //
        const { data, error } = await supabase.auth.signInWithOtp({
          email: email,
          options: {
            // set this to false if you do not want the user to be automatically signed up
            shouldCreateUser: allowCreateUser,
            emailRedirectTo: 'https://spectra.reviews/auth/magic-link'
          }
        })

        if (error) {
          throw error
        }

        return data
      } catch (error) {
        // Handle specific error cases with user-friendly messages
        if (error.status === 422 && !allowCreateUser) {
          this.authError = {
            message: 'No account found with this email address'
          }
        } else {
          this.authError = error
        }
        throw this.authError
      } finally {
        this.loading = false
      }
    },

    async handleLoginPhone(phoneContry, phone, allowCreateUser = false) {
      this.authError = null
      try {
        this.loading = true
        const number = buildPhoneNumber(phoneContry, phone)

        const { data, error } = await supabase.auth.signInWithOtp({
          phone: number,
          options: {
            // set this to false if you do not want the user to be automatically signed up
            shouldCreateUser: allowCreateUser,
            emailRedirectTo: 'https://spectra.reviews/app'
          }
        })

        if (error) throw error
        return data
      } catch (error) {
        // Handle specific error cases with user-friendly messages
        if (error.status === 422 && !allowCreateUser) {
          this.authError = {
            message: 'No account found with this phone number',
            status: 422
          }
        } else {
          this.authError = error
        }
        throw this.authError
      } finally {
        this.loading = false
      }
    },

    async handleLoginOTP(mode, credentials, { skipInit = false } = {}) {
      const { email, phoneCountry, phone, otp } = credentials

      this.authError = null
      try {
        this.loading = true

        if (mode === 'phone') {
          const number = buildPhoneNumber(phoneCountry, phone)
          const {
            data: { session },
            error
          } = await supabase.auth.verifyOtp({
            phone: number,
            token: otp,
            type: 'sms'
          })
          if (error) throw error

          if (!skipInit) this.handleSuccessfulLogin(session)
          return session
        } else {
          const {
            data: { session },
            error
          } = await supabase.auth.verifyOtp({
            email: email,
            token: otp,
            type: 'email'
          })
          if (error) throw error

          if (!skipInit) this.handleSuccessfulLogin(session)
          return session
        }
      } catch (error) {
        console.log('Login OTP error', error)
        this.authError = error
        throw error // Re-throw so calling code can handle it
      } finally {
        this.loading = false
      }
    },

    handleSuccessfulLogin(session) {
      this.updateSessionFromSupabaseSession(session)
      this.user = session.user
      this.getUserInfoFromSupabase()
      this.startSessionWatchdog()

      // Identify user in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { identify } = usePostHog()
          if (session.user?.id) {
            identify(session.user.id, {
              email: session.user.email,
              phone: session.user.phone
            })
          }
        })
      }
    },

    // logoutUser() {
    //   return signOut(auth)
    //     .then(() => {
    //       console.log('User signed out')
    //       return true
    //     })
    //     .catch((error) => {
    //       console.log(error.message)
    //       return error
    //     })
    // },
    clear() {
      this.authError = null
      this.loading = false
    },

    // Soft session invalidation — clears in-memory state and navigates to
    // auth, but does NOT call supabase.auth.signOut(). The refresh token
    // survives in localStorage so Supabase can auto-recover on the next
    // cold start via INITIAL_SESSION. Use this for transient failures
    // (network errors, iOS resume) where the session may still be valid.
    async invalidateSession() {
      this.stopSessionWatchdog()

      this.user = null
      this.session = null
      this.userNotAuthed = true
      this.userLoaded = true

      this.clearLocalStorageCache()

      // Reset PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { reset } = usePostHog()
          reset()
        })
      }

      // Navigate to auth
      const currentRoute = this.router?.currentRoute?.value
      if (currentRoute && currentRoute.name !== 'auth') {
        await this.router.push({ name: 'auth' })
      }
    },

    // Hard session clear — for intentional logouts only. Calls signOut()
    // which destroys the refresh token, requiring full re-authentication.
    async clearSession() {
      this.stopSessionWatchdog()

      // Remove this device's push tokens while the session can still
      // authorise the delete - after signOut() RLS blocks it
      try {
        const { usePushNotifications } = await import(
          '@/composables/usePushNotifications'
        )
        await usePushNotifications().unregisterPush()
      } catch (error) {
        console.error('[storeAuth] Error unregistering push:', error)
      }

      // Clear state first so the SIGNED_OUT event handler is a no-op
      this.authError = null
      this.user = null
      this.session = null
      this.userNotAuthed = true
      this.userLoaded = true

      // Sign out from Supabase (clears its own localStorage session)
      try {
        await supabase.auth.signOut()
      } catch (error) {
        console.error('[storeAuth] Error during signOut:', error.message)
      }

      // Clear cached Pinia store data from localStorage
      this.clearLocalStorageCache()

      // Reset PostHog user tracking
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { reset } = usePostHog()
          reset()
        })
      }

      // Navigate to auth AFTER cleanup to avoid racing with state changes
      const currentRoute = this.router?.currentRoute?.value
      if (currentRoute && currentRoute.name !== 'auth') {
        await this.router.push({ name: 'auth' })
      }
    },

    // Clear Pinia store caches from localStorage
    clearLocalStorageCache() {
      const clearableKeys = [
        'storeAuth',
        'storeHomeFeed',
        'storeReviews',
        'storeLists',
        'storeTitleLogs',
        'storeProfile',
        'storeFeed',
        'storeTitlesUser',
        'storeUsers'
      ]

      console.warn('[storeAuth] Clearing localStorage cache on logout')

      for (const key of clearableKeys) {
        try {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key)
          }
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    },

    // Revalidate session and refresh stores after app resumes from background.
    // Includes a delay to let iOS networking reconnect, retry logic, and a
    // mutex to prevent racing with the watchdog's visibility handler.
    async refreshAfterResume() {
      // Skip if another validation is already in progress
      if (this._isValidatingSession) {
        console.log('[storeAuth] Skipping resume refresh — validation already in progress')
        return true
      }

      this._isValidatingSession = true
      console.log('[storeAuth] Refreshing after app resume')

      try {
        // Wait for iOS networking to reconnect after background
        await new Promise((r) => setTimeout(r, 2000))

        // Check if the Supabase session is still valid
        let { data: sessionData, error: sessionError } =
          await supabase.auth.getSession()

        // Retry once after 2s on failure
        if (sessionError || !sessionData?.session) {
          console.warn('[storeAuth] Session check failed on resume, retrying in 2s')
          await new Promise((r) => setTimeout(r, 2000))
          ;({ data: sessionData, error: sessionError } =
            await supabase.auth.getSession())
        }

        if (sessionError) {
          console.error('[storeAuth] Session error on resume after retry:', sessionError)
          await this.invalidateSession()
          return false
        }

        if (!sessionData?.session) {
          console.log('[storeAuth] No valid session on resume after retry')
          await this.invalidateSession()
          return false
        }

        // Session is valid - update our local session reference
        this.session = sessionData.session
        this._lastSessionValidation = Date.now()
        this._watchdogFailureCount = 0

        // Refresh user data from Supabase to ensure it's current
        const { data: userData, error: userError } =
          await supabase.auth.getUser()

        if (userError || !userData?.user) {
          console.error('[storeAuth] User fetch error on resume:', userError)
          await this.invalidateSession()
          return false
        }

        // Update user data
        this.user = userData.user

        // Re-initialize all dependent stores with fresh data
        await this.refreshStores()

        console.log('[storeAuth] Successfully refreshed after resume')
        return true
      } catch (error) {
        console.error('[storeAuth] Error during resume refresh:', error)
        return false
      } finally {
        this._isValidatingSession = false
      }
    },

    // Refresh all dependent stores (used after resume)
    async refreshStores() {
      if (!this.user) return

      const storeProfile = useStoreProfile()
      const storeReviews = useStoreReviews()
      const storeLists = useStoreLists()
      const storeFeed = useStoreFeed()

      // Refresh profile first
      await storeProfile.fetch()

      // Skip other stores if user is pending deletion
      if (storeProfile.currentUserPendingDeletion) {
        return
      }

      // Refresh other stores in parallel (using refreshAfterResume to reconnect realtime)
      await Promise.all([
        storeReviews.fetch ? storeReviews.fetch() : Promise.resolve(),
        storeFeed.refreshAfterResume(),
        storeLists.refreshAfterResume()
      ])
    },

    // Session watchdog — periodically validates session and proactively refreshes tokens
    startSessionWatchdog() {
      if (this._sessionWatchdogTimer) return

      console.log('[storeAuth] Starting session watchdog')

      const validateSession = async () => {
        // Skip if resume handler is already validating
        if (this._isValidatingSession) {
          console.log('[storeAuth] Watchdog: skipping — resume validation in progress')
          return
        }

        this._isValidatingSession = true
        try {
          const { data, error } = await supabase.auth.getSession()

          if (error || !data?.session) {
            this._watchdogFailureCount++
            console.warn(
              `[storeAuth] Watchdog: session invalid (failure ${this._watchdogFailureCount}/3)`
            )

            // Only invalidate after 3 consecutive failures to ride out
            // transient network issues
            if (this._watchdogFailureCount >= 3) {
              console.warn('[storeAuth] Watchdog: 3 consecutive failures, soft-invalidating')
              await this.invalidateSession()
            }
            return
          }

          // Success — reset failure counter
          this._watchdogFailureCount = 0
          this._lastSessionValidation = Date.now()

          // Proactively refresh if token expires within 2 minutes
          const expiresAt = data.session.expires_at
          if (expiresAt) {
            const expiresIn = expiresAt * 1000 - Date.now()
            if (expiresIn < 2 * 60 * 1000) {
              console.log('[storeAuth] Watchdog: token expiring soon, refreshing')
              const { error: refreshError } =
                await supabase.auth.refreshSession()
              if (refreshError) {
                console.error(
                  '[storeAuth] Watchdog: refresh failed',
                  refreshError
                )
                // Don't immediately invalidate — let the failure counter
                // handle it on the next cycle
                this._watchdogFailureCount++
              }
            }
          }
        } catch (err) {
          console.error('[storeAuth] Watchdog error:', err)
          this._watchdogFailureCount++
        } finally {
          this._isValidatingSession = false
        }
      }

      // Run every 5 minutes
      this._sessionWatchdogTimer = setInterval(validateSession, 5 * 60 * 1000)

      // Also validate when tab/app becomes visible
      this._visibilityHandler = () => {
        if (document.visibilityState === 'visible' && this.user) {
          validateSession()
        }
      }
      document.addEventListener('visibilitychange', this._visibilityHandler)
    },

    stopSessionWatchdog() {
      if (this._sessionWatchdogTimer) {
        clearInterval(this._sessionWatchdogTimer)
        this._sessionWatchdogTimer = null
      }
      if (this._visibilityHandler) {
        document.removeEventListener('visibilitychange', this._visibilityHandler)
        this._visibilityHandler = null
      }
      this._isValidatingSession = false
      this._watchdogFailureCount = 0
    },

    // Wait for auth to be ready (either authenticated or confirmed not authenticated)
    async waitForAuth() {
      // If already loaded, resolve immediately
      if (this.userLoaded) {
        // Also ensure session is available if user is authenticated
        if (this.user && !this.session) {
          try {
            const { data } = await supabase.auth.getSession()
            this.session = data.session
          } catch (error) {
            console.error('Error getting session:', error)
          }
        }
        return this.user
      }

      // Otherwise, wait for userLoaded to become true
      return new Promise((resolve) => {
        const checkAuth = () => {
          if (this.userLoaded) {
            // Also ensure session is available if user is authenticated
            if (this.user && !this.session) {
              supabase.auth
                .getSession()
                .then(({ data }) => {
                  this.session = data.session
                  resolve(this.user)
                })
                .catch((error) => {
                  console.error('Error getting session:', error)
                  resolve(this.user)
                })
            } else {
              resolve(this.user)
            }
          } else {
            // Check again in 50ms
            setTimeout(checkAuth, 50)
          }
        }

        checkAuth()
      })
    }
  }
})
